import { useCallback, useEffect, useRef, useState } from "react";
import { fetchSuggestions } from "../api/chatClient";
import { getRecordPrompts, getStaticSuggestions } from "../suggestions";

/**
 * Merges ChatMS's personalised prompts with the static engine.
 *
 * Ranking, highest payoff first:
 *   1. On-screen record prompts - the biller types nothing at all. Nothing beats this,
 *      however strong the personalisation signal, so these always lead.
 *   2. Personalised prompts from GetSuggestions, ranked by which tools this biller
 *      actually uses. Server already round-robins topics and attaches sReason to the
 *      first prompt of each - we preserve its order rather than re-sorting.
 *   3. Static top-up, so the full range stays visible.
 *
 * Degradation is the whole point of the structure below: GetSuggestions is an
 * enhancement, never a dependency. If it's slow, failing, or not deployed, the widget
 * opens instantly on the static list and nobody notices.
 */

const MAX_PROMPTS = 5;
const MAX_RECENT = 3;

/** ChatMS reports bPersonalised:false below its confidence threshold. Don't claim otherwise. */
function normalise(payload) {
  if (!payload) return null;

  // Accept both spellings. ChatMS returns `Suggestions`; an older build returned `Suggested`,
  // and a frontend deployed ahead of the service would otherwise silently show an empty
  // personalised list while still reporting bPersonalised:true.
  const raw =
    payload.Suggestions || payload.suggestions || payload.Suggested || payload.suggested || [];
  const prompts = raw
    .map((s) =>
      typeof s === "string"
        ? { text: s, reason: null, topic: null }
        : {
            text: s.sPrompt || s.sText || s.Prompt || "",
            reason: s.sReason || null,
            topic: s.sTopic || null,
          }
    )
    .filter((s) => s.text);

  const recent = (payload.RecentTopics || payload.recentTopics || [])
    .map((t) => ({
      conversationID: t.ConversationID ?? t.conversationID ?? null,
      title: t.sTitle || t.Title || "",
    }))
    .filter((t) => t.title && t.conversationID != null);

  return {
    // Both conditions, not just the flag. If the payload shape ever drifts and prompts parse
    // to nothing, claiming personalisation would label a purely static list as tailored —
    // exactly the kind of quiet dishonesty that costs trust when a biller notices.
    personalised: payload.bPersonalised === true && prompts.length > 0,
    prompts,
    recent: recent.slice(0, MAX_RECENT),
  };
}

export default function useSuggestions({ open, practiceID, userID, pageContext }) {
  const [server, setServer] = useState(null);
  const abortRef = useRef(null);
  // Fetched once per session, not per open - the signal is usage history, which
  // doesn't meaningfully move between two panel opens a minute apart.
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!open || fetchedRef.current || !practiceID || !userID) return undefined;
    fetchedRef.current = true;

    const controller = new AbortController();
    abortRef.current = controller;

    fetchSuggestions({ practiceID, userID, signal: controller.signal })
      .then((payload) => setServer(normalise(payload)))
      .catch(() => setServer(null)); // already swallowed in the client; belt and braces

    return () => controller.abort();
  }, [open, practiceID, userID]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const build = useCallback(() => {
    const out = [];
    const seen = new Set();
    const add = (text, reason = null) => {
      if (out.length >= MAX_PROMPTS || !text || seen.has(text)) return;
      seen.add(text);
      out.push({ text, reason });
    };

    // 1. Zero typing wins outright.
    getRecordPrompts(pageContext).forEach((t) => add(t));

    // 2. Personalised, in the server's order - it already round-robins topics so a
    //    denial specialist still sees an AR prompt. Re-sorting would undo that.
    if (server?.personalised) {
      server.prompts.forEach((p) => add(p.text, p.reason));
    }

    // 3. Keep the range visible.
    getStaticSuggestions(pageContext, MAX_PROMPTS).forEach((t) => add(t));

    return out;
  }, [server, pageContext]);

  return {
    suggestions: build(),
    /** True only when ChatMS said so. Drives honest labelling in the UI. */
    isPersonalised: server?.personalised === true,
    recentTopics: server?.recent || [],
  };
}
